// src/pages/admin/PermissionsSection.tsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Lock,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  User,
  BarChart3,
  Search,
} from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

type Role = "Admin" | "Manager" | "User";

type UserRow = {
  id: number;
  email: string;
  name: string;
  role: Role;
  function?: string | null;
};

type Report = {
  id: number;
  name: string;
  description?: string | null;
  sql?: string | null;
  procedure?: string | null;
  active: boolean;
  createdAt: string;
};

type PermissionSnapshot = { userId: number; reportIds: number[] };

function cleanBearer(t?: string | null) {
  let raw = (t || "").trim();
  if (raw.toLowerCase().startsWith("bearer ")) raw = raw.slice(7).trim();
  return raw;
}

export function PermissionsSection() {
  const { toast } = useToast();

  const [openIds, setOpenIds] = useState<Set<number>>(new Set());
  const [users, setUsers] = useState<UserRow[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [permMap, setPermMap] = useState<Map<number, Set<number>>>(new Map());

  const [query, setQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState<"ALL" | Role>("ALL");

  // Pagination utilisateurs
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(15);

  // Pagination rapports par utilisateur
  const [reportPage, setReportPage] = useState<Map<number, number>>(new Map());
  const [reportPageSize, setReportPageSize] = useState<Map<number, number>>(new Map());

  // Recherche rapports par utilisateur
  const [reportQuery, setReportQuery] = useState<Map<number, string>>(new Map());

  // ✅ Tri “autorisés en premier” ACTIVÉ UNIQUEMENT APRÈS SAUVEGARDE
  const [sortSelectedFirstFor, setSortSelectedFirstFor] = useState<Set<number>>(new Set());

  const getReportPage = (userId: number) => reportPage.get(userId) ?? 1;
  const setReportPageFor = (userId: number, p: number) =>
    setReportPage((m) => {
      const n = new Map(m);
      n.set(userId, Math.max(1, p));
      return n;
    });

  const getReportPageSize = (userId: number) => reportPageSize.get(userId) ?? 10;
  const setReportPageSizeFor = (userId: number, sz: number) =>
    setReportPageSize((m) => {
      const n = new Map(m);
      n.set(userId, sz);
      return n;
    });

  const getReportQueryFor = (userId: number) => reportQuery.get(userId) ?? "";
  const setReportQueryFor = (userId: number, q: string) =>
    setReportQuery((m) => {
      const n = new Map(m);
      n.set(userId, q);
      return n;
    });

  const authHeaders = useMemo(() => {
    const token = cleanBearer(localStorage.getItem("auth_token"));
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // charger users (sans Admin)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/users`, { headers: { Accept: "application/json", ...authHeaders } });
        const d = await res.json();
        const nonAdmins = (d as UserRow[]).filter((u) => u.role !== "Admin");
        setUsers(nonAdmins);
      } catch {
        toast({ title: "Erreur", description: "Chargement des utilisateurs échoué", variant: "destructive" });
      }
    })();
  }, [authHeaders, toast]);

  // charger reports
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/reports`, { headers: { Accept: "application/json", ...authHeaders } });
        const d = await res.json();
        setReports(d as Report[]);
      } catch {
        toast({ title: "Erreur", description: "Chargement des rapports échoué", variant: "destructive" });
      }
    })();
  }, [authHeaders, toast]);

  // charger snapshot permissions
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/permissions`, { headers: { Accept: "application/json", ...authHeaders } });
        const d = (await res.json()) as PermissionSnapshot[];
        const mp = new Map<number, Set<number>>();
        d.forEach((s) => mp.set(s.userId, new Set(s.reportIds || [])));
        setPermMap(mp);
      } catch {
        toast({ title: "Attention", description: "Endpoint /permissions introuvable (backend ?)", variant: "destructive" });
      }
    })();
  }, [authHeaders, toast]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      const matchQ = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
      const matchG = groupFilter === "ALL" ? true : u.role === groupFilter;
      return matchQ && matchG;
    });
  }, [users, query, groupFilter]);

  // tri + pagination utilisateurs
  const sortedUsers = useMemo(() => {
    const arr = [...filteredUsers];
    arr.sort((a, b) => {
      const n = a.name.localeCompare(b.name);
      if (n !== 0) return n;
      return a.email.localeCompare(b.email);
    });
    return arr;
  }, [filteredUsers]);

  const totalUsers = sortedUsers.length;
  const totalUserPages = Math.max(1, Math.ceil(totalUsers / pageSize));
  const currentUserPage = Math.min(page, totalUserPages);

  const paginatedUsers = useMemo(() => {
    const start = (currentUserPage - 1) * pageSize;
    return sortedUsers.slice(start, start + pageSize);
  }, [sortedUsers, currentUserPage, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [query, groupFilter, users, pageSize]);

  const togglePanel = (userId: number) => {
    setOpenIds((s) => {
      const n = new Set(s);
      if (n.has(userId)) {
        n.delete(userId);
      } else {
        n.add(userId);
        if (!reportPage.has(userId)) setReportPageFor(userId, 1);
        if (!reportPageSize.has(userId)) setReportPageSizeFor(userId, 5);
        if (!reportQuery.has(userId)) setReportQueryFor(userId, "");
      }
      return n;
    });
  };

  const hasAccess = (userId: number, reportId: number) => permMap.get(userId)?.has(reportId) ?? false;

  const toggleAccess = (userId: number, reportId: number, value: boolean) => {
    setPermMap((m) => {
      const next = new Map(m);
      const set = new Set(next.get(userId) ?? new Set<number>());
      value ? set.add(reportId) : set.delete(reportId);
      next.set(userId, set);
      return next;
    });
  };

  // --- FILTRAGE des rapports (par utilisateur)
  // ⚠️ Ici on NE trie PAS par “autorisés d’abord” tant que l’utilisateur n’a pas cliqué “Sauvegarder”.
  const getFilteredReports = useCallback(
    (userId: number): Report[] => {
      const q = getReportQueryFor(userId).trim().toLowerCase();
      const base = !q
        ? reports
        : reports.filter((r) => {
            const n = (r.name || "").toLowerCase();
            const d = (r.description || "").toLowerCase();
            return n.includes(q) || d.includes(q);
          });

      // Si on a sauvegardé pour cet utilisateur → trier les autorisés en premier
      if (sortSelectedFirstFor.has(userId)) {
        const selected = permMap.get(userId) ?? new Set<number>();
        const arr = [...base];
        arr.sort((a, b) => {
          const ha = selected.has(a.id) ? 1 : 0;
          const hb = selected.has(b.id) ? 1 : 0;
          if (ha !== hb) return hb - ha; // sélectionnés en premier
          const byName = (a.name || "").localeCompare(b.name || "");
          if (byName !== 0) return byName;
          return a.id - b.id;
        });
        return arr;
      }

      // Pas de tri spécial avant la première sauvegarde
      return base;
    },
    [reports, reportQuery, sortSelectedFirstFor, permMap]
  );

  // Helpers rapports paginés (sur la liste filtrée, puis triée si applicable)
  const getUserReportsSlice = useCallback(
    (userId: number) => {
      const pz = getReportPageSize(userId);
      const pg = getReportPage(userId);

      const filtered = getFilteredReports(userId);

      const total = filtered.length;
      const totalPages = Math.max(1, Math.ceil(total / pz));
      const safePg = Math.min(pg, totalPages);
      const start = (safePg - 1) * pz;
      const slice = filtered.slice(start, start + pz);
      return {
        items: slice,
        total,
        page: safePg,
        totalPages,
        pageSize: pz,
        query: getReportQueryFor(userId).trim(),
      };
    },
    [getFilteredReports, reportPage, reportPageSize, reportQuery]
  );

  // --- Tout sélectionner / Tout retirer sur la LISTE FILTRÉE
  const applyBulkOnFiltered = (userId: number, select: boolean) => {
    const filtered = getFilteredReports(userId);
    if (!filtered.length) return;

    setPermMap((m) => {
      const next = new Map(m);
      const set = new Set(next.get(userId) ?? new Set<number>());
      for (const r of filtered) {
        if (select) set.add(r.id);
        else set.delete(r.id);
      }
      next.set(userId, set);
      return next;
    });
  };

  const saveUser = async (u: UserRow) => {
    try {
      const ids = Array.from(permMap.get(u.id) || []);
      const res = await fetch(`${API_BASE}/permissions/${u.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Accept: "application/json", ...authHeaders },
        body: JSON.stringify({ reportIds: ids }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.detail || `Sauvegarde impossible (${res.status})`);
      const count = ids.length;

      // ✅ Après succès : activer le tri “autorisés d’abord” pour cet utilisateur
      setSortSelectedFirstFor((s) => {
        const n = new Set(s);
        n.add(u.id);
        return n;
      });
      // Revenir à la page 1 pour voir immédiatement les rapports autorisés en tête
      setReportPageFor(u.id, 1);

      toast({ title: "Permissions sauvegardées", description: `${u.name} a ${count} rapport(s) autorisé(s).` });
    } catch (e: any) {
      toast({ title: "Erreur", description: e?.message || "Sauvegarde échouée", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Lock className="h-5 w-5 text-foreground" />
        <h2 className="text-2xl font-semibold">Gestion des Permissions Utilisateur</h2>
      </div>

      {/* Toolbar (recherche, filtre, taille page) */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 max-w-md w-full">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un utilisateur (nom ou email)…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="flex items-center gap-3">
          <Select value={groupFilter} onValueChange={(v) => { setGroupFilter(v as any); setPage(1); }}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filtrer par groupe" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tous les groupes</SelectItem>
              <SelectItem value="Manager">MANAGER</SelectItem>
              <SelectItem value="User">USER</SelectItem>
            </SelectContent>
          </Select>

          {totalUserPages > 1 && (
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Taille page" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 / page</SelectItem>
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="15">15 / page</SelectItem>
                <SelectItem value="20">20 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Liste utilisateurs paginée */}
      <div className="space-y-4">
        {paginatedUsers.map((u) => {
          const isOpen = openIds.has(u.id);
          const count = permMap.get(u.id)?.size ?? 0;

          const rPag = getUserReportsSlice(u.id);

          return (
            <Card key={u.id} className="p-0 overflow-hidden border-glass">
              <button
                onClick={() => togglePanel(u.id)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/40 transition"
              >
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-primary" />
                  <div>
                    <div className="font-medium">
                      {u.name} <span className="text-muted-foreground">({u.role})</span>
                    </div>
                    <div className="text-xs text-muted-foreground">{count} rapport(s)</div>
                  </div>
                </div>
                {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </button>

              {isOpen && (
                <div className="px-5 pb-5">
                  <div className="rounded-lg border bg-card">
                    {/* En-tête + actions */}
                    <div className="px-5 py-4 font-semibold flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <span>Rapports disponibles :</span>
                      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                        {/* Recherche par rapport (par utilisateur) */}
                        <div className="flex items-center gap-2">
                          <Search className="h-4 w-4 text-muted-foreground" />
                          <Input
                            className="w-[260px]"
                            placeholder="Rechercher un rapport…"
                            value={getReportQueryFor(u.id)}
                            onChange={(e) => {
                              setReportQueryFor(u.id, e.target.value);
                              setReportPageFor(u.id, 1);
                            }}
                          />
                        </div>

                        <div className="flex items-center gap-2">
                          {/* Bulk sur la liste FILTRÉE */}
                          <Button variant="outline" size="sm" onClick={() => applyBulkOnFiltered(u.id, true)}>
                            Tout sélectionner
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => applyBulkOnFiltered(u.id, false)}>
                            Tout retirer
                          </Button>

                          {/* Taille page rapports (par utilisateur) */}
                          {rPag.totalPages > 1 && (
                            <Select
                              value={String(rPag.pageSize)}
                              onValueChange={(v) => {
                                setReportPageSizeFor(u.id, Number(v));
                                setReportPageFor(u.id, 1);
                              }}
                            >
                              <SelectTrigger className="w-[140px]">
                                <SelectValue placeholder="Taille page" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="5">5 / page</SelectItem>
                                <SelectItem value="10">10 / page</SelectItem>
                                <SelectItem value="20">20 / page</SelectItem>
                                <SelectItem value="50">50 / page</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Liste rapports paginée (sur filtré, et trié “autorisés d’abord” uniquement après sauvegarde) */}
                    <div className="divide-y">
                      {rPag.items.map((r) => (
                        <div key={r.id} className="flex items-center justify-between px-5 py-3">
                          <div className="flex items-center gap-3">
                            <BarChart3 className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <div className="font-medium">{r.name}</div>
                              {!!r.description && <div className="text-xs text-muted-foreground">{r.description}</div>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground">Accès</span>
                            <Switch
                              checked={hasAccess(u.id, r.id)}
                              onCheckedChange={(v) => toggleAccess(u.id, r.id, v)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination rapports */}
                    {rPag.totalPages > 1 && (
                      <div className="px-5 py-4 flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                          {rPag.total} rapport{rPag.total > 1 ? "s" : ""} • page {rPag.page} / {rPag.totalPages}
                          {rPag.query ? ` • filtre: "${rPag.query}"` : ""}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={rPag.page <= 1}
                            onClick={() => setReportPageFor(u.id, rPag.page - 1)}
                            className="gap-1"
                          >
                            <ChevronLeft className="h-4 w-4" /> Précédent
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={rPag.page >= rPag.totalPages}
                            onClick={() => setReportPageFor(u.id, rPag.page + 1)}
                            className="gap-1"
                          >
                            Suivant <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="px-5 pb-5">
                      <Button className="gap-2" onClick={() => saveUser(u)}>
                        Sauvegarder permissions de {u.name}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Pagination utilisateurs */}
      {totalUserPages > 1 && (
        <div className="mt-2 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {totalUsers} utilisateur{totalUsers > 1 ? "s" : ""} • page {currentUserPage} / {totalUserPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentUserPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" /> Précédent
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={currentUserPage >= totalUserPages}
              onClick={() => setPage((p) => Math.min(totalUserPages, p + 1))}
              className="gap-1"
            >
              Suivant <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
